import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiLookupResetToken, apiRedeemResetToken } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const LOGO = (
  <img
    src="https://static.wixstatic.com/media/47e68b_9ba20c13e75f4752b5585a413624d1cc~mv2.png/v1/fill/w_342,h_72,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Icon%20%2B%20Lutrom%20%2B%20Engineering.png"
    alt="Lutrom Engineering"
    className="h-9 w-auto inline-block"
    style={{ filter: 'invert(1) brightness(2)' }}
  />
);

/**
 * /reset-password/:token — public, no-auth page where a user chooses a
 * fresh password after clicking the link in their reset email.
 *
 * Mirrors the invite flow (see Invite.jsx) — a successful reset returns
 * a JWT so the user is signed straight in and dropped on their role home.
 */
const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const [state, setState] = useState({ status: 'loading' });
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    apiLookupResetToken(token)
      .then((d) => alive && setState({ status: 'ready', info: d }))
      .catch((e) => {
        if (!alive) return;
        const code = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (code === 410) setState({ status: 'expired', detail });
        else if (code === 404) setState({ status: 'notfound', detail });
        else setState({ status: 'error', detail: detail || 'Could not load reset link' });
      });
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return toast.error('Password must be at least 8 characters');
    if (pwd !== confirm) return toast.error("Passwords don't match");
    setSubmitting(true);
    try {
      const { token: jwt, user } = await apiRedeemResetToken(token, pwd);
      setSession(jwt, user);
      toast.success('Password updated — you’re signed in.');
      const home = user.role === 'admin' ? '/admin'
                 : user.role === 'employee' ? '/employee'
                 : user.role === 'subcontractor' ? '/subcontractor'
                 : '/customer';
      navigate(home, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <div className="bg-slate-900 px-6 py-5 text-center">{LOGO}</div>
        <div className="p-6">
          {state.status === 'loading' && (
            <div className="text-sm text-slate-500 text-center py-8">Checking your link…</div>
          )}
          {state.status === 'expired' && (
            <Notice
              tone="amber"
              icon={AlertTriangle}
              title="This reset link has expired"
              body={state.detail || 'Reset links only stay live for 1 hour. Request a new one and try again.'}
              action={<Link to="/forgot-password" className="text-sm underline">Request a new link</Link>}
            />
          )}
          {state.status === 'notfound' && (
            <Notice
              tone="rose"
              icon={AlertTriangle}
              title="Reset link not found"
              body="This link is invalid. Make sure you copied it in full from the email — or request a new one below."
              action={<Link to="/forgot-password" className="text-sm underline">Request a new link</Link>}
            />
          )}
          {state.status === 'error' && (
            <Notice tone="rose" icon={AlertTriangle} title="Something went wrong" body={state.detail} />
          )}
          {state.status === 'ready' && (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500">Password reset</div>
                <h1 className="text-2xl font-display tracking-tight text-slate-900 mt-1">
                  Choose a new password
                </h1>
                <p className="text-sm text-slate-600 mt-2">
                  You&rsquo;re resetting the password for your Lutrom Engineering portal account.
                </p>
                <div className="mt-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded text-xs text-slate-700 font-mono break-all">
                  {state.info.email}
                </div>
              </div>
              <div>
                <Label htmlFor="rp-pwd">New password</Label>
                <Input
                  id="rp-pwd"
                  type="password"
                  className="mt-1.5"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  autoFocus
                  data-testid="reset-password-pwd-input"
                  minLength={8}
                  required
                />
              </div>
              <div>
                <Label htmlFor="rp-confirm">Confirm new password</Label>
                <Input
                  id="rp-confirm"
                  type="password"
                  className="mt-1.5"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-password-confirm-input"
                  minLength={8}
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-slate-900 hover:bg-slate-800 h-11"
                data-testid="reset-password-submit-btn"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                {submitting ? 'Updating…' : 'Update password'}
              </Button>
              <p className="text-[11px] text-slate-500 text-center">
                Use at least 8 characters. We&rsquo;ll sign you in as soon as you submit.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const Notice = ({ tone, icon: Icon, title, body, action }) => {
  const map = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
  };
  return (
    <div className={`border rounded-md p-4 flex items-start gap-3 ${map[tone] || ''}`}>
      <Icon className="h-5 w-5 mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold text-sm mb-1">{title}</div>
        <div className="text-sm opacity-90">{body}</div>
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
};

export default ResetPassword;
